import * as dateFnsCompat from 'utils/date-fns-compat';

import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { defaultIndustrySettings, industryType, routes } from '../../../constants';
import { cpdPointsForCpdCategory, cpdPointsSum, handleInvalidForm, industryCpdDate, isBusinessAdmin } from '../../../services';
import { businessActions, businessCpdCategorySelector, businessIndustryRoles, businessSelector } from '../../../state/modules/business';
import { userActions } from '../../../state/modules/user';
import { branchFuncs } from '../../../utils';
import { Icon } from '../../components';
import { CpdAllocation, CpdAnniversarySetting, CpdPointsSetting } from '../../partials';
import Loading from '../loading';
import { defaultSelectedAoaForIndustry, getIndustryCpdCategoriesByName } from './industry';

const OverlayCloseBtn = ({ overlayRef }) => (
  <Button onClick={() => overlayRef.hide()} className='pull-right full-width close-overlay' bsStyle='plain'>
    <Icon icon='close' />
  </Button>
)

const cpdAnniversaryTooltip = (overlayRef) => (
  <Tooltip id='cpd-anniversary-tooltip'>
    <OverlayCloseBtn overlayRef={overlayRef} />
    <p><strong>CPD Anniversary</strong></p>
    <p>Specify the date you need to achieve your CPD by each year.</p>
  </Tooltip>
);

const cpdPointsTooltip = (overlayRef) => (
  <Tooltip id='cpd-points-tooltip'>
    <OverlayCloseBtn overlayRef={overlayRef} />
    <p><strong>Required annual CPD hours</strong></p>
    <p>Specify the number of CPD hours you need to achieve each year.</p>
  </Tooltip>
);

const allocatePointsTooltip = (overlayRef) => (
  <Tooltip id='allocate-points-tooltip'>
    <OverlayCloseBtn overlayRef={overlayRef} />
    <p><strong>CPD allocation</strong></p>
    <p>Specify the number of CPD hours you need for each CPD category you selected.</p>
  </Tooltip>
);

const TooltipOverlay = ({
  title,
  overlay,
  setOverlayRef
}) => (
    <Row className='no-gutter'>
      <Col xs={12} sm={8}>
        <h4>{title}</h4>
      </Col>
      <Col xs={12} sm={3}>
        <p>
          <OverlayTrigger ref={(ref) => setOverlayRef(ref)} placement='bottom' overlay={overlay}>
            <Button bsStyle='plain'>
              <Icon icon='help' className='hide-on-ie' /> <span>What's this?</span>
            </Button>
          </OverlayTrigger>
        </p>
      </Col>
    </Row>
  );

const BoxedColumn = ({ children }) => (
  <Col
    xs={10} xsOffset={1}
    sm={8} smOffset={2}
    md={6} mdOffset={3}
    className='boxed'>
    {children}
  </Col>
);

const CpdRequirementsForm = branch(
  ({ industryRolesIsFetching }) => industryRolesIsFetching,
  renderComponent(Loading)
)(({
  cpdCategorys,
  componentRef,
  isBusinessAdmin,
  isMinimumBased,
  onCpdCategorysChange,
  onIndustryDateChange,
  onIndustryChange,
  selectedCpdCategorys,
  setCpdCategorys,
  industry,
  validation,
  validationMessages,
  shouldDisplayAoaAlocation,
  cpdAnniversaryOverlayRef,
  cpdPointsOverlayRef,
  allocatePointsOverlayRef,
  setCpdAnniversaryOverlayRef,
  setCpdPointsOverlayRef,
  setAllocatePointsOverlayRef
}) => (
    <form
      ref={(form) => componentRef && (componentRef.formRef = form)}
      noValidate
      onSubmit={setCpdCategorys}>
      <BoxedColumn>
        <TooltipOverlay
          title='Let us know your CPD year end date.'
          overlay={cpdAnniversaryTooltip(cpdAnniversaryOverlayRef)}
          setOverlayRef={setCpdAnniversaryOverlayRef} />
        <CpdAnniversarySetting
          cpdDate={industryCpdDate(industry)}
          onIndustryDateChange={onIndustryDateChange} />
      </BoxedColumn>
      {
        isMinimumBased ?
          null :
          [<BoxedColumn key="cpd-points-setting-box">
            <TooltipOverlay
              title='How many CPD hours do you need to earn each year?'
              overlay={cpdPointsTooltip(cpdPointsOverlayRef)}
              setOverlayRef={setCpdPointsOverlayRef} />
            <CpdPointsSetting {...{
              industry,
              onIndustryChange,
              validationMessages
            }} />
          </BoxedColumn>,
          shouldDisplayAoaAlocation ?
            <BoxedColumn key="cpd-allocation-box">
              <TooltipOverlay
                title='Allocate your CPD hours to your CPD categories.'
                overlay={allocatePointsTooltip(allocatePointsOverlayRef)}
                setOverlayRef={setAllocatePointsOverlayRef} />
              <div className='divider' />
              {
                fp.isEmpty(cpdCategorys) ?
                  null :
                  <CpdAllocation {...{
                    cpdCategorys,
                    industryCpdPoints: industry.cpdPoints,
                    onCpdCategorysChange,
                    validation,
                    validationMessages
                  }} />
              }
            </BoxedColumn> : null
          ]
      }
      <Col xs={12}>
        <Row className='no-gutter submit-row'>
          <Col xs={12} md={6} mdOffset={3}>
            <Button type='submit' className='large'>
              Continue
            </Button>
            <p>
              <LinkContainer
                exact
                className='full'
                to={
                  isBusinessAdmin ?
                    routes.onboarding.teamInvite :
                    routes.app.dashboard
                }>
                <Button bsStyle='plain'>
                  {
                    isBusinessAdmin ?
                      'I\'ll do this later - skip this step' :
                      'I\'ll do this later - take me to Caddie'
                  }
                </Button>
              </LinkContainer>
            </p>
          </Col>
        </Row>
      </Col>
    </form>
  ));

/**
 * Allows users to specify their initial areas of advice during on-boarding.
 */
const CpdRequirements = (props) => (
  <section id='onboarding'>
    <Grid>
      <Row>
        <Col xs={12} md={8} mdOffset={2}>
          <h1 className="text-center">Set up your CPD requirements</h1>
          <p>You can change these at any time</p>
        </Col>
      </Row>
      <Row>
        <CpdRequirementsForm {...props} />
      </Row>
    </Grid>
  </section>
);

const autoAllocateCpdPoints = (cpdPoints) => branchFuncs(
  fp.overEvery([() => cpdPoints > 0, fp.head]),
  fp.flow(
    fp.over([
      fp.tail,
      fp.head,
      fp.flow(fp.size, fp.divide(cpdPoints), fp.ceil),
    ]),
    ([cpdCategorys, cpdCategory, totalCpdPoints]) =>
      fp.flow(
        autoAllocateCpdPoints(cpdPoints - totalCpdPoints),
        fp.concat([{ ...cpdCategory, totalCpdPoints }])
      )(cpdCategorys)
  ),
  fp.stubArray
);

export default compose(
  connect(({
    global: {
      onboarding: {
        selectedCpdCategorys
      }
    },
    industry: {
      cpdCategorys: industryCpdCategorys,
      industryRolesIsFetching,
      cpdCategoryIsFetching,
      industriesWithCpdCategorys
    },
    session,
    user: {
      currentUser,
      isFetching,
      profile,
    }
  }) => ({
    currentUser,
    currentUserIndustryName: profile ? profile.stat.industryName : "",
    industryCpdCategorys,
    selectedCpdCategorys,
    session,
    industriesWithCpdCategorys,
    isFetchingResource: isFetching || industryRolesIsFetching || cpdCategoryIsFetching
  })),
  withProps(({
    industryCpdCategorys,
    currentUserIndustryName,
    industriesWithCpdCategorys
  }) => ({
    currentUserIndustryId: fp.flow(
      fp.map('industry'),
      fp.find((industry) => industry.name === currentUserIndustryName),
      fp.get('id')
    )(industriesWithCpdCategorys),
    currentUserIndustryType: fp.flow(
      fp.map('industry'),
      fp.find((industry) => industry.name === currentUserIndustryName),
      fp.get('industryType')
    )(industriesWithCpdCategorys),
    industryCpdCategorys: industryCpdCategorys.length === 0 
      ? getIndustryCpdCategoriesByName(currentUserIndustryName, industriesWithCpdCategorys)
      : industryCpdCategorys,
  })),
  withProps(({
    currentUser,
    currentUserIndustryType,
    industryCpdCategorys,
    selectedCpdCategorys,
    match: {
      params: {
        industryIdentifier
      }
    },
    industriesWithCpdCategorys
  }) => ({
    isBusinessAdmin: isBusinessAdmin(currentUser),
    shouldDisplayAoaAlocation: defaultSelectedAoaForIndustry(industryIdentifier, industryCpdCategorys, selectedCpdCategorys, industriesWithCpdCategorys).length > 1,
    selectedCpdCategorys: fp.filter(({ id }) =>
      fp.includes(id, defaultSelectedAoaForIndustry(industryIdentifier, industryCpdCategorys, selectedCpdCategorys, industriesWithCpdCategorys))
    )(industryCpdCategorys),
    isMinimumBased: currentUserIndustryType === industryType.Minimum
  })),
  branch(
    ({ isBusinessAdmin }) => isBusinessAdmin,
    compose(
      connect((state) => ({ business: businessSelector(state) })),
      branch(
        ({ business }) => !business,
        renderComponent(Loading)
      ),
      connect((state) => ({
        cpdCategorys: businessCpdCategorySelector(state),
        industryRoles: businessIndustryRoles(state),
        industryRolesIsFetching: state.business.industryRolesIsFetching
      }), (dispatch, { business: { id } }) => ({
        getIndustryRoles: () => bindActionCreators(businessActions.business.getIndustryRoles, dispatch)(id),
        setCpdCategorys: ({
          industry,
          ...payload
        }) => bindActionCreators(businessActions.business.setCpdCategorys, dispatch)({
          ...payload,
          applyChangesToUser: true,
          businessId: id,
          industry
        })
      })),
      withProps({
        transform: (cpdCategory) => ({
          ...cpdCategory,
          businessId: 0
        })
      })
    ),
    compose(
      connect(({
        user: {
          currentUserIndustryRoles: industryRoles,
          currentUserIndustryRolesIsFetching: industryRolesIsFetching
        }
      }) => ({
        industryRoles,
        industryRolesIsFetching
      }), (dispatch) => ({
        getIndustryRoles: bindActionCreators(userActions.user.getIndustryRoles, dispatch),
        setCpdCategorys: ({
          industry,
          ...payload
        }) => bindActionCreators(userActions.user.setCpdCategorys, dispatch)({
          ...payload,
          industry
        })
      })),
      withProps({
        transform: (cpdCategory) => ({
          ...cpdCategory,
          userId: 0
        })
      })
    )
  ),
  lifecycle({
    componentDidMount() {
      const {
        industryRoles,
        getIndustryRoles
      } = this.props;

      if (industryRoles.length === 0) {
        getIndustryRoles();
      }

      this.setState({ componentRef: this });
    }
  }),
  branch(
    ({
      industryRoles,
      industryRolesIsFetching,
      isFetchingResource,
      selectedCpdCategorys
    }) => {
      return isFetchingResource || industryRolesIsFetching || !industryRoles.length || selectedCpdCategorys.length === 0
    },
    renderComponent(Loading)
  ),
  withProps(({
    industryRoles
  }) => ({
    industry: {
      ...defaultIndustrySettings,
      ...fp.head(industryRoles)
    }
  })),
  withStateHandlers(({
    industryRoles,
    isMinimumBased,
    selectedCpdCategorys,
    industry: {
      cpdPoints,
      ...industry
    },
    transform
  }) => ({
    cpdCategorys: fp.flow(
      (aoas) => isMinimumBased ? aoas : autoAllocateCpdPoints(cpdPoints)(aoas),
      fp.map(({
        id: cpdCategoryId,
        name,
        minimumCpd,
        totalCpdPoints = minimumCpd
      }) => transform({
        cpdCategoryId,
        name,
        totalCpdPoints,
        minimumCpd
      }))
    )(selectedCpdCategorys),
    industry: {
      ...industry,
      cpdPoints
    },
    validation: {},
    validationMessages: {},
    cpdAnniversaryOverlayRef: null,
    cpdPointsOverlayRef: null,
    allocatePointsOverlayRef: null
  }), {
      onCpdCategorysChange: ({
        cpdCategorys
      }) => (e) => ({
        cpdCategorys: fp.map(({
          cpdCategoryId,
          minimumCpd,
          ...cpdCategory
        }) => {
          if (cpdCategoryId === parseInt(e.name, 10)) {
            return {
              ...cpdCategory,
              cpdCategoryId,
              minimumCpd,
              totalCpdPoints: parseInt(e.value, 10) > minimumCpd ? parseInt(e.value, 10) : minimumCpd || 0
            };
          }
          else {
            return {
              ...cpdCategory,
              cpdCategoryId
            };
          }
        })(cpdCategorys)
      }),
      onIndustryDateChange: ({
        industry
      }) => (cpdDate) => ({
        industry: {
          ...industry,
          cpdDay: dateFnsCompat.getDate(cpdDate),
          cpdMonth: dateFnsCompat.getMonth(cpdDate) + 1
        }
      }),
      onIndustryChange: ({
        industry,
        cpdCategorys,
        shouldDisplayAoaAlocation
      }) => (field, value, defaultValue) => {
        const newValue = parseInt(value, 10)
        if (shouldDisplayAoaAlocation) {
          return {
            industry: {
              ...industry,
              [field]: newValue || defaultValue
            }
          }
        } else {
          return {
            industry: {
              ...industry,
              [field]: newValue || defaultValue
            },
            cpdCategorys: fp.map((cpdCategory) => ({
              ...cpdCategory,
              totalCpdPoints: newValue || 0
            }))(cpdCategorys)
          }
        }
      },
      /**
       * Validates the form and sets the validation state.
       *
       * Returns true if all fields are valid, false otherwise.
       */
      validateForm: ({
        cpdCategorys,
        industry: {
          cpdPoints
        }
      }, {
        componentRef,
        selectedCpdCategorys
      }) => (callback) => {
        const invalidCpdCategorys = fp.flow(
          fp.map(({ id }) => [id, (cpdPointsForCpdCategory(id)(cpdCategorys)) > 0]),
          fp.filter((x) => !x[1]),
          fp.fromPairs
        )(selectedCpdCategorys);

        const validation = {
          totalCpdPoints: cpdPoints > 0,
          assignedCpdPoints: cpdPoints === cpdPointsSum(cpdCategorys),
          ...invalidCpdCategorys
        };

        if (fp.all(fp.identity)(validation)) {
          callback();
        } else if (componentRef && componentRef.formRef) {
          handleInvalidForm(componentRef.formRef);
        }

        return {
          validation,
          validationMessages: {
            totalCpdPoints: validation.totalCpdPoints ?
              null :
              'You must undertake at least one CPD hour.',
            assignedCpdPoints: validation.assignedCpdPoints ?
              null :
              'You must assign exactly this many CPD hours to your selected CPD categories.',
            ...fp.mapValues(
              () => 'You must undertake at least one CPD hour in each selected CPD category.'
            )(invalidCpdCategorys)
          }
        };
      },
      setCpdAnniversaryOverlayRef: () => (ref) => ({ cpdAnniversaryOverlayRef: ref }),
      setCpdPointsOverlayRef: () => (ref) => ({ cpdPointsOverlayRef: ref }),
      setAllocatePointsOverlayRef: () => (ref) => ({ allocatePointsOverlayRef: ref })
    }),
  withHandlers({
    setCpdCategorys: ({
      cpdCategorys,
      setCpdCategorys,
      isMinimumBased,
      industry,
      validateForm,
      currentUserIndustryId
    }) => (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (isMinimumBased) {
        setCpdCategorys({
          industryId: currentUserIndustryId,
          cpdCategorys,
          industry
        });
      } else {
        validateForm(() => {
          setCpdCategorys({
            industryId: currentUserIndustryId,
            cpdCategorys,
            industry
          })
        });
      }
    }
  })
)(CpdRequirements);



// WEBPACK FOOTER //
// ./src/containers/pages/onboarding/cpd-requirements.js